import {
  collection,
  doc,
  addDoc,
  getDoc,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../Firebase/Config';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ReturnReason =
  | 'wrong_item'
  | 'defective'
  | 'damaged'
  | 'not_as_described'
  | 'size_issue'
  | 'changed_mind'
  | 'late_delivery'
  | 'other';

export interface ReturnItem {
  productId: string;
  productName: string;
  productSku?: string;
  productImage?: string;
  variantId?: string;
  variantSelections?: Array<{ variantType: string; variantValue: string }>;
  quantity: number; // how many of this item the customer wants to return
  unitPrice: number;
  totalPrice: number;
}

export type ReturnStatus =
  | 'pending'       // submitted, awaiting admin review
  | 'approved'      // admin approved, stock will be restored
  | 'rejected'      // admin rejected the request
  | 'completed';    // stock restored / refund issued

export interface ReturnRequest {
  id?: string;
  orderId: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  items: ReturnItem[];
  reason: ReturnReason;
  damageNotes?: string;           // required when reason is 'damaged' or 'defective'
  additionalNotes?: string;
  status: ReturnStatus;
  refundAmount: number;
  createdAt?: Date;
  updatedAt?: Date;
  adminNotes?: string;            // filled by admin when processing
}

// ─── Labels (match admin RETURN_REASON_LABELS exactly) ───────────────────────

export const RETURN_REASON_LABELS: Record<ReturnReason, string> = {
  wrong_item: 'Wrong Item Received',
  defective: 'Defective Product',
  damaged: 'Arrived Damaged',
  not_as_described: 'Not as Described',
  size_issue: 'Size / Fit Issue',
  changed_mind: 'Changed My Mind',
  late_delivery: 'Arrived Too Late',
  other: 'Other Reason',
};

export const RETURN_STATUS_LABELS: Record<ReturnStatus, string> = {
  pending: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  completed: 'Completed',
};

// Reasons that require damage notes (mirrors admin isDamaged check)
export const DAMAGE_REASONS: ReturnReason[] = ['damaged', 'defective'];

// Reasons where FULL items must be returned (can't do partial)
export const FULL_RETURN_REASONS: ReturnReason[] = ['wrong_item'];

const RETURN_REQUESTS_COLLECTION = 'return_requests';

// ─── Service ─────────────────────────────────────────────────────────────────

export class OrderReturnService {

  /**
   * Fetch a single order by its Firestore document ID.
   * Used to pre-populate the return form.
   */
  static async getOrderById(orderId: string) {
    const orderRef = doc(db, 'orders', orderId);
    const snap = await getDoc(orderRef);
    if (!snap.exists()) return null;

    const data = snap.data();

    // Convert Firestore timestamps to Date (same helper as admin mapDocToOrder)
    const toDate = (ts: any): Date | undefined => {
      if (!ts) return undefined;
      if (ts.toDate) return ts.toDate();
      if (ts instanceof Date) return ts;
      if (ts.seconds !== undefined) return new Date(ts.seconds * 1000);
      return undefined;
    };

    return {
      id: snap.id,
      orderNumber: data.orderNumber || '',
      customerName: data.customerName || '',
      customerEmail: data.customerEmail || '',
      items: data.items || [],
      subtotal: data.subtotal || 0,
      taxAmount: data.taxAmount || 0,
      shippingCost: data.shippingCost || 0,
      discountAmount: data.discountAmount || 0,
      totalAmount: data.totalAmount || 0,
      status: data.status || 'pending',
      paymentStatus: data.paymentStatus || 'pending',
      paymentMethod: data.paymentMethod || '',
      shippingAddress: data.shippingAddress || {},
      orderDate: toDate(data.orderDate) || new Date(),
      isWarehouseAllocated: data.isWarehouseAllocated || false,
    };
  }

  /**
   * Look up orders by customer email so they can find their order number.
   */
  static async getOrdersByEmail(email: string) {
    const q = query(
      collection(db, 'orders'),
      where('customerEmail', '==', email),
      orderBy('orderDate', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => {
      const data = d.data();
      const toDate = (ts: any): Date => {
        if (!ts) return new Date();
        if (ts.toDate) return ts.toDate();
        if (ts.seconds !== undefined) return new Date(ts.seconds * 1000);
        return new Date();
      };
      return {
        id: d.id,
        orderNumber: data.orderNumber || '',
        customerName: data.customerName || '',
        customerEmail: data.customerEmail || '',
        status: data.status || 'pending',
        totalAmount: data.totalAmount || 0,
        orderDate: toDate(data.orderDate),
        items: data.items || [],
        isWarehouseAllocated: data.isWarehouseAllocated || false,
        paymentStatus: data.paymentStatus || 'pending',
      };
    });
  }

  /**
   * Search order by order number + email (same as OrderTracking page).
   */
  static async findOrder(orderNumber: string, email: string) {
    const q = query(
      collection(db, 'orders'),
      where('orderNumber', '==', orderNumber),
      where('customerEmail', '==', email)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;

    const d = snap.docs[0];
    const data = d.data();
    const toDate = (ts: any): Date => {
      if (!ts) return new Date();
      if (ts.toDate) return ts.toDate();
      if (ts.seconds !== undefined) return new Date(ts.seconds * 1000);
      return new Date();
    };
    return {
      id: d.id,
      orderNumber: data.orderNumber || '',
      customerName: data.customerName || '',
      customerEmail: data.customerEmail || '',
      status: data.status || 'pending',
      totalAmount: data.totalAmount || 0,
      orderDate: toDate(data.orderDate),
      items: data.items || [],
      subtotal: data.subtotal || 0,
      taxAmount: data.taxAmount || 0,
      shippingCost: data.shippingCost || 0,
      discountAmount: data.discountAmount || 0,
      paymentStatus: data.paymentStatus || 'pending',
      paymentMethod: data.paymentMethod || '',
      shippingAddress: data.shippingAddress || {},
      isWarehouseAllocated: data.isWarehouseAllocated || false,
    };
  }

  /**
   * Check whether the customer already has an open return request
   * for this order.
   */
  static async getExistingReturn(orderId: string): Promise<ReturnRequest | null> {
    const q = query(
      collection(db, RETURN_REQUESTS_COLLECTION),
      where('orderId', '==', orderId)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;

    const d = snap.docs[0];
    const data = d.data();
    const toDate = (ts: any): Date | undefined => {
      if (!ts) return undefined;
      if (ts.toDate) return ts.toDate();
      if (ts.seconds !== undefined) return new Date(ts.seconds * 1000);
      return undefined;
    };
    return {
      id: d.id,
      orderId: data.orderId,
      orderNumber: data.orderNumber,
      customerName: data.customerName,
      customerEmail: data.customerEmail,
      items: data.items || [],
      reason: data.reason,
      damageNotes: data.damageNotes,
      additionalNotes: data.additionalNotes,
      status: data.status,
      refundAmount: data.refundAmount || 0,
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
      adminNotes: data.adminNotes,
    };
  }

  /**
   * Submit a return request.
   *
   * This writes a document to `return_requests`.
   * The admin app / Cloud Function then:
   *   1. Reviews the request
   *   2. Updates order status to 'returned'
   *   3. Calls restoreStockToWarehouse() (same as admin updateOrder does)
   *
   * This mirrors the admin's:
   *   OrderService.updateOrder(..., returnReason, damageNotes)
   *   → restoreStockToWarehouse(orderId, order.items, returnReason, damageNotes)
   */
  static async submitReturnRequest(request: Omit<ReturnRequest, 'id' | 'status' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const data = {
      ...request,
      status: 'pending' as ReturnStatus,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Mirror admin fields so the processing function has everything it needs
      damageNotes: request.damageNotes || null,
      additionalNotes: request.additionalNotes || null,
      adminNotes: null,
    };

    const docRef = await addDoc(collection(db, RETURN_REQUESTS_COLLECTION), data);
    return docRef.id;
  }

  /**
   * Determine which orders are eligible for return.
   * Mirrors the admin rule: only 'delivered' orders can be returned.
   * Returns must be within 30 days of delivery.
   */
  static isEligibleForReturn(order: { status: string; orderDate: Date }): { eligible: boolean; reason?: string } {
    if (order.status !== 'delivered') {
      return {
        eligible: false,
        reason: `Only delivered orders can be returned. Current status: ${order.status}.`,
      };
    }

    const daysSinceOrder = Math.floor(
      (Date.now() - order.orderDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceOrder > 30) {
      return {
        eligible: false,
        reason: `Return window has expired. Returns are accepted within 30 days of delivery.`,
      };
    }

    return { eligible: true };
  }

  /**
   * Calculate refund amount for selected items.
   */
  static calculateRefund(items: ReturnItem[]): number {
    return items.reduce((sum, item) => sum + item.totalPrice, 0);
  }
}